import { isBasketItemExpired } from '@store/basket/utils/isBasketItemExpired';
import { SpaPackageBasketItem } from 'shared-types/Basket';

type GuestLike = {
  id?: number | string | null;
  title?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  gender?: string | null;
};

type SelectionLike = {
  option?: { id?: number } | null;
  extra?: { id?: number } | null;
  startDate?: string | null;
  startTime?: string | null;
  guestDetails?: GuestLike | null;
};

type SelectionGroupLike = {
  isExtras?: boolean;
  configurationGroup?: { id?: number } | null;
  selections?: (SelectionLike | null)[] | null;
};

type SpaDayLike = {
  type?: string;
  spaPackage?: { id?: number } | null;
  checkIn?: string | null;
  expireAt?: string | null;
  bookable?: { status?: string } | null;
  note?: string | null;
  guestDetails?: (GuestLike | null)[] | null;
  selectionGroups?: (SelectionGroupLike | null)[] | null;
};

const listOf = <T>(value: unknown): T[] =>
  Array.isArray(value) ? value.filter((entry): entry is T => !!entry) : [];

const trimmed = (value?: string | number | null) => String(value ?? '').trim();

const toArrival = (checkIn: string) => {
  const [date, time = ''] = checkIn.trim().replace('T', ' ').split(' ');
  const minute = time.slice(0, 5);

  return minute && minute !== '00:00' ? `${date} ${minute}` : date;
};

const guestKey = (guest?: GuestLike | null) =>
  guest ? [guest.title, guest.firstName, guest.lastName, guest.gender].map(trimmed).join('|') : '';

const selectionKeys = (spaDay: SpaDayLike) =>
  listOf<SelectionGroupLike>(spaDay.selectionGroups).flatMap((group) =>
    listOf<SelectionLike>(group.selections).map((selection) =>
      [
        group.isExtras ? 'extras' : group.configurationGroup?.id,
        selection.option?.id,
        selection.extra?.id,
        selection.startDate,
        trimmed(selection.startTime).slice(0, 5),
        guestKey(selection.guestDetails),
      ]
        .map(trimmed)
        .join('|')
    )
  );

const hasSameEntries = (held: string[], requested: string[]) => {
  const sortedHeld = [...held].sort();
  const sortedRequested = [...requested].sort();

  return (
    sortedHeld.length === sortedRequested.length &&
    sortedHeld.every((entry, index) => entry === sortedRequested[index])
  );
};

const holdsRequestedGuestIds = (held: GuestLike[], requested: GuestLike[]) => {
  const unmatchedHeldIds = held
    .filter((guest) => guest.id != null)
    .map((guest) => trimmed(guest.id));

  return requested
    .filter((guest) => guest.id != null)
    .every((guest) => {
      const index = unmatchedHeldIds.indexOf(trimmed(guest.id));
      if (index < 0) return false;
      unmatchedHeldIds.splice(index, 1);
      return true;
    });
};

const isStillHeld = (item: SpaDayLike, now: number) =>
  item.bookable?.status !== 'failed' &&
  typeof item.expireAt === 'string' &&
  !Number.isNaN(Date.parse(item.expireAt)) &&
  !isBasketItemExpired(item, now);

const isSameRequest = (held: SpaDayLike, requested: SpaDayLike) => {
  const heldGuests = listOf<GuestLike>(held.guestDetails);
  const requestedGuests = listOf<GuestLike>(requested.guestDetails);

  return (
    held.spaPackage?.id === requested.spaPackage?.id &&
    typeof held.checkIn === 'string' &&
    typeof requested.checkIn === 'string' &&
    toArrival(held.checkIn) === toArrival(requested.checkIn) &&
    trimmed(held.note) === trimmed(requested.note) &&
    hasSameEntries(heldGuests.map(guestKey), requestedGuests.map(guestKey)) &&
    holdsRequestedGuestIds(heldGuests, requestedGuests) &&
    hasSameEntries(selectionKeys(held), selectionKeys(requested))
  );
};

export function withoutSpaDaysAlreadyInBasket(
  basketItems: unknown,
  spaDays: SpaPackageBasketItem[],
  now = Date.now()
): SpaPackageBasketItem[] {
  const heldSpaDays = listOf<SpaDayLike>(basketItems).filter(
    (item) => item.type === 'spa-packages' && isStillHeld(item, now)
  );

  return spaDays.filter(
    (spaDay) => !heldSpaDays.some((held) => isSameRequest(held, spaDay as SpaDayLike))
  );
}
